// Centrale Zustand-store: scenario, selectie, tekenmodus en undo/redo.
// Mutaties die de blok-collectie wijzigen pushen automatisch een snapshot
// op de undo-stack zodat Ctrl+Z het scenario terugdraait.

import { create } from "zustand";
import {
  BLOCK_TYPE_DEFAULTS,
  CURRENT_SCHEMA_VERSION,
  type Block,
  type BlockType,
  type Scenario,
  type ScenarioView,
} from "../types/scenario";
import { randomUUID } from "../utils/uuid";
import {
  hashScenarioForShadow,
  runYearAggregateInWorker,
  type YearAggregateJob,
  type YearAggregateResult,
} from "../shadow";
import {
  getCachedYearResult,
  putCachedYearResult,
} from "../storage/yearResultCache";
import {
  deletePersonalScenario as dbDeletePersonal,
  listPersonalScenarios,
  savePersonalScenario,
} from "../storage/personalScenarios";
import * as api from "../storage/api";

// localStorage-sleutels en defaults. Deze worden gelezen tijdens de
// initialisatie van `useStore` (zie shadowOpacity hieronder), dus ze moeten
// boven die `create<Store>(...)`-aanroep staan — anders TDZ.
const LAST_SCENARIO_KEY = "schaduwtool.lastScenarioId";
const SHADOW_OPACITY_KEY = "schaduwtool.shadowOpacity";
const SHADOW_OPACITY_DEFAULT = 45;
const SHADOW_OPACITY_MIN = 10;
const SHADOW_OPACITY_MAX = 100;

export type DrawMode =
  | "idle"
  | "draw-polygon"
  | "draw-rectangle"
  | "move-vertex"
  | "move-block"
  | "delete";

interface DrawingTempState {
  /** Polygoon-vertices in lng/lat tijdens het tekenen. */
  points: [number, number][];
  /** Voor rechthoek-modus: het eerste vastgezette hoekpunt. */
  rectangleAnchor: [number, number] | null;
  /** Live cursor-positie (voor preview-rendering). */
  cursor: [number, number] | null;
}

interface State {
  scenario: Scenario;
  selectedBlockId: string | null;
  drawMode: DrawMode;
  /** Type dat aan een nieuw getekend blok wordt toegekend. */
  newBlockType: BlockType;
  drawing: DrawingTempState;
  undoStack: Block[][];
  redoStack: Block[][];
  /** Gekozen datum en tijd voor de tijdslider — alleen UI-state, geen
   * onderdeel van het scenario zelf. Jaar staat vast op REFERENCE_YEAR. */
  currentDayOfYear: number;
  currentMinuteOfDay: number;
  /** Welke hoofdweergave actief is — 2D-kaart of 3D-model. */
  viewMode: "2d" | "3d";

  /** Jaaraggregaat-resultaten + voortgang. */
  yearResult: YearAggregateResult | null;
  yearResultHash: string | null;
  yearJob: { running: boolean; pct: number; phase: string; cancel?: () => void };

  /** Heatmap-overlay zichtbaar in 2D-modus. */
  heatmapVisible: boolean;

  /**
   * Doorzichtigheid van de schaduw-overlay in 2D (10–100 %). Wordt nog
   * vermenigvuldigd met blockingFactor per schaduw. Persoonlijke voorkeur,
   * bewaard in localStorage onder `schaduwtool.shadowOpacity`.
   */
  shadowOpacity: number;

  /** Basemap-keuze: PDOK luchtfoto of PDOK BRT-A standaard topografisch. */
  basemap: "sat" | "topo";

  /** UI-zichtbaarheid voor de zijpanelen. Gebruikt op smalle schermen
   * (tablet/mobiel) om de panelen weg te kunnen klappen; op desktop is
   * dit puur cosmetisch. */
  leftPanelOpen: boolean;
  rightPanelOpen: boolean;

  /** Lijst persoonlijke scenario's uit IndexedDB. */
  personalScenarios: Scenario[];
  /** Lijst publieke scenario's (samenvatting) opgehaald van de backend. */
  publicScenarios: api.ScenarioSummary[];
  /** ID van het scenario dat als "default" geldt voor het eerste bezoek
   * (server-instelling, admin-beheerd). */
  defaultScenarioId: string | null;
  /** URL (of data-URL) van het logo dat bovenin het linkerpaneel verschijnt. */
  logoUrl: string | null;
  /** Ingelogde admin-user, of null. */
  authUser: api.AuthUser | null;
  /** True zolang we nog niet zeker weten of er een sessie is. */
  authChecking: boolean;
}

/** Vast referentiejaar voor de tijdslider (zie §2.4 van het ontwerp). */
export const REFERENCE_YEAR = 2025;

interface Actions {
  setScenarioName: (name: string) => void;
  setScenarioDescription: (description: string) => void;
  setScenarioAuthor: (author: string) => void;
  setScenarioView: (view: Partial<ScenarioView>) => void;
  selectBlock: (id: string | null) => void;
  setDrawMode: (mode: DrawMode) => void;
  setNewBlockType: (type: BlockType) => void;

  // Drawing helpers
  drawAddPoint: (point: [number, number]) => void;
  drawPopPoint: () => void;
  drawSetCursor: (point: [number, number] | null) => void;
  drawSetRectangleAnchor: (point: [number, number] | null) => void;
  drawReset: () => void;
  finishPolygonDraw: () => void;
  finishRectangleDraw: (corner: [number, number]) => void;

  // Block mutations (alle pushen een undo-snapshot)
  updateBlock: (id: string, patch: Partial<Block>) => void;
  removeBlock: (id: string) => void;
  setBlockPolygon: (id: string, polygon: [number, number][]) => void;
  moveBlockBy: (id: string, dLng: number, dLat: number) => void;

  // Undo / redo
  undo: () => void;
  redo: () => void;

  // Tijd
  setCurrentDayOfYear: (doy: number) => void;
  setCurrentMinuteOfDay: (m: number) => void;

  // Weergave
  setViewMode: (mode: "2d" | "3d") => void;

  // Jaarberekening
  startYearComputation: () => Promise<void>;
  cancelYearComputation: () => void;
  setHeatmapVisible: (v: boolean) => void;
  setShadowOpacity: (v: number) => void;

  // Scenario-beheer
  refreshPersonalList: () => Promise<void>;
  refreshPublicList: () => Promise<void>;
  newScenario: () => void;
  loadScenario: (scenario: Scenario) => void;
  duplicateAsPersonal: (source: Scenario) => Promise<void>;
  deletePersonal: (id: string) => Promise<void>;
  loadPublicScenario: (id: string) => Promise<void>;
  publishCurrentScenario: () => Promise<void>;
  deletePublicScenario: (id: string) => Promise<void>;

  // Auth
  refreshAuth: () => Promise<void>;
  loginAdmin: (username: string, password: string) => Promise<void>;
  logoutAdmin: () => Promise<void>;

  // App-instellingen
  refreshAppSettings: () => Promise<void>;
  setDefaultScenarioId: (id: string | null) => Promise<void>;
  setLogoUrl: (url: string | null) => Promise<void>;
  loadInitialScenario: () => Promise<void>;

  // UI
  setLeftPanelOpen: (v: boolean) => void;
  setRightPanelOpen: (v: boolean) => void;
  setBasemap: (b: "sat" | "topo") => void;
}

export type Store = State & Actions;

const HILVERSUM: ScenarioView = {
  centerLat: 52.2292,
  centerLng: 5.1669,
  zoom: 17,
  rotation: 0,
};

function newScenario(): Scenario {
  const now = new Date().toISOString();
  return {
    schemaVersion: CURRENT_SCHEMA_VERSION,
    id: randomUUID(),
    name: "Nieuw scenario",
    description: "",
    author: "",
    createdAt: now,
    updatedAt: now,
    isPublic: false,
    view: HILVERSUM,
    blocks: [],
  };
}

const MAX_UNDO = 50;

// Helper: clone the blocks array so undo-snapshots stay immutable t.o.v. live state.
function cloneBlocks(blocks: Block[]): Block[] {
  return blocks.map((b) => ({ ...b, polygon: b.polygon.map((p) => [...p] as [number, number]) }));
}

export const useStore = create<Store>((set, get) => ({
  scenario: newScenario(),
  selectedBlockId: null,
  drawMode: "idle",
  newBlockType: "gebouw",
  drawing: { points: [], rectangleAnchor: null, cursor: null },
  undoStack: [],
  redoStack: [],
  currentDayOfYear: 172, // 21 juni — zomerzonnewende
  currentMinuteOfDay: 720, // 12:00
  viewMode: "2d",

  yearResult: null,
  yearResultHash: null,
  yearJob: { running: false, pct: 0, phase: "" },
  heatmapVisible: false,
  shadowOpacity: readShadowOpacity(),
  basemap: "sat",

  personalScenarios: [],
  publicScenarios: [],
  defaultScenarioId: null,
  logoUrl: null,
  authUser: null,
  authChecking: true,

  // Op desktop default beide panelen open; op smalle schermen zorgt CSS
  // ervoor dat ze als overlay verschijnen en is dichtklappen logischer.
  leftPanelOpen: true,
  rightPanelOpen: true,

  setCurrentDayOfYear: (doy) => set({ currentDayOfYear: clampInt(doy, 1, 365) }),
  setCurrentMinuteOfDay: (m) => set({ currentMinuteOfDay: clampInt(m, 0, 1439) }),
  setViewMode: (mode) => set({ viewMode: mode }),

  setHeatmapVisible: (v) => set({ heatmapVisible: v }),

  setShadowOpacity: (v) => {
    const clamped = clampInt(v, SHADOW_OPACITY_MIN, SHADOW_OPACITY_MAX);
    set({ shadowOpacity: clamped });
    try {
      localStorage.setItem(SHADOW_OPACITY_KEY, String(clamped));
    } catch {
      // localStorage kan in een private window of bij quota-limieten falen;
      // verlies van de voorkeur is geen blocker.
    }
  },

  startYearComputation: async () => {
    const { scenario, yearJob } = get();
    if (yearJob.running) return;
    const hash = hashScenarioForShadow(scenario);
    // Cache-lookup eerst.
    const cached = await getCachedYearResult(hash);
    if (cached) {
      set({ yearResult: cached, yearResultHash: hash });
      return;
    }
    // Start de Worker-run.
    let job: YearAggregateJob | null = null;
    try {
      job = runYearAggregateInWorker(scenario, { referenceYear: 2025 });
    } catch (err) {
      console.error("[store] kon jaarberekening niet starten:", err);
      return;
    }
    job.onProgress((pct, phase) => {
      set((s) =>
        s.yearJob.running ? { yearJob: { ...s.yearJob, pct, phase } } : s,
      );
    });
    set({
      yearJob: {
        running: true,
        pct: 0,
        phase: "Starten…",
        cancel: () => job!.cancel(),
      },
    });
    try {
      const result = await job.promise;
      await putCachedYearResult(result);
      set({
        yearResult: result,
        yearResultHash: hash,
        yearJob: { running: false, pct: 100, phase: "Klaar" },
      });
    } catch (err) {
      console.error("[store] jaarberekening mislukt:", err);
      set({ yearJob: { running: false, pct: 0, phase: "Mislukt" } });
    }
  },

  cancelYearComputation: () => {
    const { yearJob } = get();
    if (yearJob.running && yearJob.cancel) {
      yearJob.cancel();
    }
    set({ yearJob: { running: false, pct: 0, phase: "Afgebroken" } });
  },

  setScenarioName: (name) =>
    set((s) => ({ scenario: { ...s.scenario, name, updatedAt: new Date().toISOString() } })),

  setScenarioDescription: (description) =>
    set((s) => ({
      scenario: { ...s.scenario, description, updatedAt: new Date().toISOString() },
    })),

  setScenarioAuthor: (author) =>
    set((s) => ({
      scenario: { ...s.scenario, author, updatedAt: new Date().toISOString() },
    })),

  setScenarioView: (view) =>
    set((s) => ({ scenario: { ...s.scenario, view: { ...s.scenario.view, ...view } } })),

  selectBlock: (id) => set({ selectedBlockId: id }),

  setDrawMode: (mode) =>
    set({
      drawMode: mode,
      drawing: { points: [], rectangleAnchor: null, cursor: null },
    }),

  setNewBlockType: (type) => set({ newBlockType: type }),

  drawAddPoint: (point) =>
    set((s) => ({ drawing: { ...s.drawing, points: [...s.drawing.points, point] } })),

  drawPopPoint: () =>
    set((s) => ({ drawing: { ...s.drawing, points: s.drawing.points.slice(0, -1) } })),

  drawSetCursor: (point) => set((s) => ({ drawing: { ...s.drawing, cursor: point } })),

  drawSetRectangleAnchor: (point) =>
    set((s) => ({ drawing: { ...s.drawing, rectangleAnchor: point } })),

  drawReset: () =>
    set({ drawing: { points: [], rectangleAnchor: null, cursor: null } }),

  finishPolygonDraw: () => {
    const { drawing, scenario, newBlockType } = get();
    if (drawing.points.length < 3) return;
    const block = createBlockFromPolygon(drawing.points, newBlockType);
    pushUndo(set, get);
    set({
      scenario: {
        ...scenario,
        blocks: [...scenario.blocks, block],
        updatedAt: new Date().toISOString(),
      },
      selectedBlockId: block.id,
      drawing: { points: [], rectangleAnchor: null, cursor: null },
      drawMode: "idle",
    });
  },

  finishRectangleDraw: (corner) => {
    const { drawing, scenario, newBlockType } = get();
    const a = drawing.rectangleAnchor;
    if (!a) return;
    // Bouw een rechthoek in lng/lat-coördinaten — geen rotatie.
    const polygon: [number, number][] = [
      [a[0], a[1]],
      [corner[0], a[1]],
      [corner[0], corner[1]],
      [a[0], corner[1]],
    ];
    if (Math.abs(corner[0] - a[0]) < 1e-7 || Math.abs(corner[1] - a[1]) < 1e-7) return;
    const block = createBlockFromPolygon(polygon, newBlockType);
    pushUndo(set, get);
    set({
      scenario: {
        ...scenario,
        blocks: [...scenario.blocks, block],
        updatedAt: new Date().toISOString(),
      },
      selectedBlockId: block.id,
      drawing: { points: [], rectangleAnchor: null, cursor: null },
      drawMode: "idle",
    });
  },

  updateBlock: (id, patch) => {
    pushUndo(set, get);
    set((s) => ({
      scenario: {
        ...s.scenario,
        blocks: s.scenario.blocks.map((b) => (b.id === id ? { ...b, ...patch } : b)),
        updatedAt: new Date().toISOString(),
      },
    }));
  },

  removeBlock: (id) => {
    pushUndo(set, get);
    set((s) => ({
      scenario: {
        ...s.scenario,
        blocks: s.scenario.blocks.filter((b) => b.id !== id),
        updatedAt: new Date().toISOString(),
      },
      selectedBlockId: s.selectedBlockId === id ? null : s.selectedBlockId,
    }));
  },

  setBlockPolygon: (id, polygon) => {
    pushUndo(set, get);
    set((s) => ({
      scenario: {
        ...s.scenario,
        blocks: s.scenario.blocks.map((b) => (b.id === id ? { ...b, polygon } : b)),
        updatedAt: new Date().toISOString(),
      },
    }));
  },

  moveBlockBy: (id, dLng, dLat) => {
    pushUndo(set, get);
    set((s) => ({
      scenario: {
        ...s.scenario,
        blocks: s.scenario.blocks.map((b) =>
          b.id === id
            ? { ...b, polygon: b.polygon.map((p) => [p[0] + dLng, p[1] + dLat] as [number, number]) }
            : b,
        ),
        updatedAt: new Date().toISOString(),
      },
    }));
  },

  undo: () => {
    const { undoStack, scenario } = get();
    if (undoStack.length === 0) return;
    const previous = undoStack[undoStack.length - 1];
    set((s) => ({
      undoStack: s.undoStack.slice(0, -1),
      redoStack: [...s.redoStack, cloneBlocks(s.scenario.blocks)],
      scenario: { ...scenario, blocks: previous, updatedAt: new Date().toISOString() },
    }));
  },

  redo: () => {
    const { redoStack, scenario } = get();
    if (redoStack.length === 0) return;
    const next = redoStack[redoStack.length - 1];
    set((s) => ({
      redoStack: s.redoStack.slice(0, -1),
      undoStack: [...s.undoStack, cloneBlocks(s.scenario.blocks)],
      scenario: { ...scenario, blocks: next, updatedAt: new Date().toISOString() },
    }));
  },

  // ---- Scenario-beheer -------------------------------------------------

  refreshPersonalList: async () => {
    const list = await listPersonalScenarios();
    set({ personalScenarios: list });
  },

  refreshPublicList: async () => {
    try {
      const list = await api.listPublicScenarios();
      set({ publicScenarios: list });
    } catch (err) {
      console.warn("[store] refreshPublicList:", err);
    }
  },

  newScenario: () => {
    set({
      scenario: newScenario(),
      selectedBlockId: null,
      drawMode: "idle",
      drawing: { points: [], rectangleAnchor: null, cursor: null },
      undoStack: [],
      redoStack: [],
      yearResult: null,
      yearResultHash: null,
      heatmapVisible: false,
    });
  },

  loadScenario: (scenario) => {
    set({
      scenario,
      selectedBlockId: null,
      drawMode: "idle",
      drawing: { points: [], rectangleAnchor: null, cursor: null },
      undoStack: [],
      redoStack: [],
      yearResult: null,
      yearResultHash: null,
      heatmapVisible: false,
    });
  },

  duplicateAsPersonal: async (source) => {
    const now = new Date().toISOString();
    const copy: Scenario = {
      ...source,
      id: randomUUID(),
      isPublic: false,
      name: source.name.endsWith("(kopie)") ? source.name : `${source.name} (kopie)`,
      createdAt: now,
      updatedAt: now,
    };
    await savePersonalScenario(copy);
    await get().refreshPersonalList();
    get().loadScenario(copy);
  },

  deletePersonal: async (id) => {
    await dbDeletePersonal(id);
    await get().refreshPersonalList();
    if (get().scenario.id === id) {
      get().newScenario();
    }
  },

  loadPublicScenario: async (id) => {
    try {
      const result = await api.fetchPublicScenario(id);
      const scenario: Scenario = {
        ...(result.data as Scenario),
        id: result.id,
        name: result.name,
        description: result.description,
        author: result.author,
        createdAt:
          typeof result.createdAt === "string"
            ? result.createdAt
            : new Date(result.createdAt).toISOString(),
        updatedAt:
          typeof result.updatedAt === "string"
            ? result.updatedAt
            : new Date(result.updatedAt).toISOString(),
        isPublic: true,
      };
      get().loadScenario(scenario);
    } catch (err) {
      console.error("[store] loadPublicScenario:", err);
    }
  },

  publishCurrentScenario: async () => {
    const scenario = get().scenario;
    const toPublish: Scenario = { ...scenario, isPublic: true };
    try {
      try {
        await api.updatePublicScenario(toPublish);
      } catch (err) {
        if (err instanceof api.ApiError && err.status === 404) {
          await api.createPublicScenario(toPublish);
        } else {
          throw err;
        }
      }
      set({ scenario: toPublish });
      await get().refreshPublicList();
    } catch (err) {
      console.error("[store] publishCurrentScenario:", err);
      throw err;
    }
  },

  deletePublicScenario: async (id) => {
    try {
      await api.deletePublicScenario(id);
      await get().refreshPublicList();
      if (get().scenario.id === id) {
        get().newScenario();
      }
    } catch (err) {
      console.error("[store] deletePublicScenario:", err);
      throw err;
    }
  },

  // ---- Auth ------------------------------------------------------------

  refreshAuth: async () => {
    try {
      const user = await api.getCurrentUser();
      set({ authUser: user, authChecking: false });
    } catch (err) {
      console.warn("[store] refreshAuth:", err);
      set({ authUser: null, authChecking: false });
    }
  },

  loginAdmin: async (username, password) => {
    const user = await api.login(username, password);
    set({ authUser: user });
  },

  logoutAdmin: async () => {
    await api.logout();
    set({ authUser: null });
  },

  // ---- App-instellingen + boot-flow ------------------------------------

  refreshAppSettings: async () => {
    try {
      const s = await api.getAppSettings();
      set({ defaultScenarioId: s.defaultScenarioId, logoUrl: s.logoUrl });
    } catch (err) {
      console.warn("[store] refreshAppSettings:", err);
    }
  },

  setDefaultScenarioId: async (id) => {
    try {
      await api.setDefaultScenarioId(id);
      set({ defaultScenarioId: id });
    } catch (err) {
      console.error("[store] setDefaultScenarioId:", err);
      throw err;
    }
  },

  setLogoUrl: async (url) => {
    try {
      const fresh = await api.setLogoUrl(url);
      set({ logoUrl: fresh.logoUrl, defaultScenarioId: fresh.defaultScenarioId });
    } catch (err) {
      console.error("[store] setLogoUrl:", err);
      throw err;
    }
  },

  loadInitialScenario: async () => {
    // Strategie:
    // 1) Als de gebruiker eerder iets bekeek, herlaad dat scenario
    //    (lastScenarioId in localStorage). Persoonlijk eerst, dan publiek.
    // 2) Anders: laad het server-default scenario, indien ingesteld.
    // 3) Anders: blijf op het lege "Nieuw scenario".
    const lastId = readLastScenarioId();
    if (lastId) {
      // Persoonlijk eerst — leeft alleen lokaal.
      const personal = get().personalScenarios.find((s) => s.id === lastId);
      if (personal) {
        get().loadScenario(personal);
        return;
      }
      // Publiek tweede.
      const isPublic = get().publicScenarios.some((s) => s.id === lastId);
      if (isPublic) {
        await get().loadPublicScenario(lastId);
        return;
      }
    }
    const def = get().defaultScenarioId;
    if (def && get().publicScenarios.some((s) => s.id === def)) {
      await get().loadPublicScenario(def);
    }
  },

  setLeftPanelOpen: (v) => set({ leftPanelOpen: v }),
  setRightPanelOpen: (v) => set({ rightPanelOpen: v }),
  setBasemap: (b) => set({ basemap: b }),
}));

function clampInt(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, Math.round(value)));
}

function readLastScenarioId(): string | null {
  try {
    return localStorage.getItem(LAST_SCENARIO_KEY);
  } catch {
    return null;
  }
}

function readShadowOpacity(): number {
  try {
    const raw = localStorage.getItem(SHADOW_OPACITY_KEY);
    if (raw === null) return SHADOW_OPACITY_DEFAULT;
    const n = Number.parseInt(raw, 10);
    if (!Number.isFinite(n)) return SHADOW_OPACITY_DEFAULT;
    return Math.min(SHADOW_OPACITY_MAX, Math.max(SHADOW_OPACITY_MIN, n));
  } catch {
    return SHADOW_OPACITY_DEFAULT;
  }
}

function createBlockFromPolygon(polygon: [number, number][], type: BlockType): Block {
  const defaults = BLOCK_TYPE_DEFAULTS[type];
  return {
    id: randomUUID(),
    name: defaults.label,
    type,
    polygon: polygon.map((p) => [...p] as [number, number]),
    heightTop: defaults.heightTop,
    heightBottom: defaults.heightBottom,
    opacity: 60,
    lightTransmission: defaults.lightTransmission,
    color: defaults.color,
  };
}

// Push een snapshot van de huidige blok-collectie op de undo-stack en wis de
// redo-stack (zoals gangbaar — een nieuwe actie verbreekt de redo-keten).
function pushUndo(
  set: (partial: Partial<State>) => void,
  get: () => Store,
): void {
  const { scenario, undoStack } = get();
  const snapshot = cloneBlocks(scenario.blocks);
  const nextStack = [...undoStack, snapshot].slice(-MAX_UNDO);
  set({ undoStack: nextStack, redoStack: [] });
}
